import { useState, useCallback, useEffect, useRef } from 'react';
import { writePref } from '../prefs/store.ts';
import { distributeWidths, percentile, capForType, isWrappable, type ColumnMeasure } from '../table/autoWidth.ts';

export interface GroupingLevel {
  fieldSlug: string;
  order: 'name' | 'count';
}

export interface TableSettings {
  colWidths: Record<string, number>;
  colOrder: string[];
  hiddenCols: string[];
  pinnedCols: string[];
  grouping: GroupingLevel[];
  // Пользователь хоть раз сам менял группировку в этом браузере. Пока false —
  // применяется дефолтная группировка из view.config; как только true —
  // приоритет у локального выбора (в т.ч. явная очистка группировки).
  groupingTouched: boolean;
  collapsedGroups: string[]; // composite keys like "status=active" or "status=active|priority=high"
  wrapRows: boolean; // авто-высота строк: текст переносится, высота подстраивается под содержимое
  density: 'compact' | 'normal'; // плотность строк: compact — меньше padding/шрифт, «Описание» в 1 строку
  pageSize: number; // pagination size; per-view, как и остальные настройки таблицы
  // Строка итогов под таблицей. Раньше показывалась всегда, когда в
  // представлении есть числовая колонка, — и занимала ~50px высоты ради
  // плейсхолдеров «итог ▾». Теперь показывается, только если хотя бы один
  // итог реально настроен либо пользователь включил строку этим флагом.
  showTotals: boolean;
  // Откуда взялись текущие ширины колонок: 'content' — подгонка под текст в одну
  // строку, 'fit' — деление ширины экрана с учётом переноса, 'manual' —
  // пользователь тянул границу колонки руками. В режиме 'fit' ширины
  // пересчитываются при изменении размера окна и состава колонок.
  widthMode: 'content' | 'fit' | 'manual';
}

/** Нормализует grouping из storage/config: массив, legacy-объект или пусто. */
function normalizeGrouping(raw: unknown): GroupingLevel[] {
  if (Array.isArray(raw)) return raw as GroupingLevel[];
  if (raw && typeof raw === 'object' && 'fieldSlug' in raw) {
    return [raw as GroupingLevel];
  }
  return [];
}

const STORAGE_KEY_PREFIX = 'lens:table:';

function loadSettings(key: string): Partial<TableSettings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + key);
    return raw ? (JSON.parse(raw) as Partial<TableSettings>) : {};
  } catch {
    return {};
  }
}

function saveSettings(key: string, settings: TableSettings) {
  // localStorage-кэш + debounced PUT на сервер (персонально для пользователя).
  // serverKey без `lens:` — store сам добавляет префикс при записи в localStorage.
  writePref('table:' + key, settings);
}

let _measureCanvas: HTMLCanvasElement | null = null;
function measureTextWidth(text: string, font: string): number {
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
  const ctx = _measureCanvas.getContext('2d');
  if (!ctx) return text.length * 8;
  ctx.font = font;
  return Math.ceil(ctx.measureText(text).width);
}

/**
 * Шрифт ячеек берём из самой таблицы, а не хардкодим: размер шрифта таблицы
 * настраивается пользователем через --lens-table-font-size, и замер по 14px
 * давал ширины мимо реальных.
 */
function cellFonts(sample: Element | null): { font: string; fontBold: string } {
  const fallback = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  if (!sample || typeof window === 'undefined' || !window.getComputedStyle) {
    return { font: fallback, fontBold: '600 ' + fallback };
  }
  const cs = window.getComputedStyle(sample);
  const size = cs.fontSize || '12px';
  const family = cs.fontFamily || '-apple-system, sans-serif';
  return { font: `${size} ${family}`, fontBold: `600 ${size} ${family}` };
}

/** Самое длинное слово в тексте — по числу символов, чтобы не мерить каждое. */
function longestWordOf(text: string): string {
  let longest = '';
  for (const word of text.split(/\s+/)) {
    if (word.length > longest.length) longest = word;
  }
  return longest;
}

export interface AutoSizeOptions {
  /**
   * 'content' — ширина под текст в одну строку (прежнее поведение);
   * 'fit' — ширина делится между колонками так, чтобы таблица влезла в экран,
   * а перенос строк был равномерным.
   */
  mode?: 'content' | 'fit';
  /** slug → тип поля. Нужен для потолков ширины и запрета переноса. */
  types?: Record<string, string>;
}

function buildSettingsFromStorage(storageKey: string, initialGrouping: GroupingLevel[]): TableSettings {
  const saved = loadSettings(storageKey);
  // Дефолтная группировка из view.config применяется, пока пользователь сам не
  // трогал группировку в этом браузере. После любого изменения (включая очистку)
  // приоритет у локально сохранённого выбора.
  // Миграция legacy-настроек (сохранены до появления флага groupingTouched):
  // тогда сохранённая группировка всегда означала пользовательский выбор,
  // поэтому непустой saved.grouping без флага считаем touched — иначе она
  // молча затиралась бы дефолтом view при первом же рендере.
  const savedTouched = (saved as { groupingTouched?: boolean }).groupingTouched;
  const savedGrouping = normalizeGrouping(saved.grouping);
  const groupingTouched =
    savedTouched === true || (savedTouched === undefined && savedGrouping.length > 0);
  const grouping = groupingTouched ? savedGrouping : initialGrouping;
  return {
    colWidths: saved.colWidths ?? {},
    colOrder: saved.colOrder ?? [],
    hiddenCols: saved.hiddenCols ?? [],
    pinnedCols: saved.pinnedCols ?? [],
    grouping,
    groupingTouched,
    collapsedGroups: (saved as { collapsedGroups?: string[] }).collapsedGroups ?? [],
    wrapRows: (saved as { wrapRows?: boolean }).wrapRows ?? true,
    density: (saved as { density?: 'compact' | 'normal' }).density ?? 'compact',
    pageSize: (saved as { pageSize?: number }).pageSize ?? 50,
    showTotals: (saved as { showTotals?: boolean }).showTotals ?? false,
    widthMode: (saved as { widthMode?: TableSettings['widthMode'] }).widthMode ?? 'manual',
  };
}

export function useTableSettings(
  storageKey: string,
  fieldSlugs: string[],
  initialGrouping: GroupingLevel[] = [],
) {
  // Держим актуальный дефолт в ref, чтобы resetSettings (стабильный useCallback)
  // мог вернуть группировку к виду из view.config, а не к пустой.
  const initialGroupingRef = useRef(initialGrouping);
  initialGroupingRef.current = initialGrouping;

  const [storedKey, setStoredKey] = useState(storageKey);
  const [settings, setSettings] = useState<TableSettings>(() => buildSettingsFromStorage(storageKey, initialGrouping));

  // При смене storageKey (переключение представления) перечитываем настройки
  // из localStorage в render-фазе, чтобы не было одного «грязного» рендера,
  // в котором persist-эффект перезаписывает чужие настройки старым state-ом.
  // Это рекомендованный React-паттерн «reset state on prop change».
  if (storedKey !== storageKey) {
    setStoredKey(storageKey);
    setSettings(buildSettingsFromStorage(storageKey, initialGrouping));
  }

  // Persist on change. Сохраняем только если settings относится к актуальному
  // ключу (после смены key первый рендер уже даст обновлённый settings).
  useEffect(() => {
    if (storedKey !== storageKey) return;
    saveSettings(storageKey, settings);
  }, [storageKey, storedKey, settings]);

  // rAF-batched: при ресайзе onResize сыпется на каждом mousemove; аккумулируем
  // последнее значение и коммитим один раз в кадр, чтобы Table/markdown ячейки
  // не перерисовывались десятки раз в секунду.
  const pendingWidthsRef = useRef<Record<string, number>>({});
  const widthRafRef = useRef<number | null>(null);
  const flushWidths = useCallback(() => {
    const pending = pendingWidthsRef.current;
    pendingWidthsRef.current = {};
    widthRafRef.current = null;
    if (Object.keys(pending).length === 0) return;
    // Ручной ресайз выключает автоматический пересчёт ширин при resize окна.
    setSettings((prev) => ({ ...prev, colWidths: { ...prev.colWidths, ...pending }, widthMode: 'manual' }));
  }, []);
  const setColWidth = useCallback((slug: string, width: number) => {
    pendingWidthsRef.current[slug] = width;
    if (widthRafRef.current === null && typeof window !== 'undefined') {
      widthRafRef.current = window.requestAnimationFrame(flushWidths);
    }
  }, [flushWidths]);
  useEffect(() => () => {
    if (widthRafRef.current !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(widthRafRef.current);
    }
  }, []);

  const setColOrder = useCallback((order: string[]) => {
    setSettings((prev) => ({ ...prev, colOrder: order }));
  }, []);

  const toggleColVisibility = useCallback((slug: string) => {
    setSettings((prev) => {
      const hidden = prev.hiddenCols.includes(slug)
        ? prev.hiddenCols.filter((s) => s !== slug)
        : [...prev.hiddenCols, slug];
      return { ...prev, hiddenCols: hidden };
    });
  }, []);

  const togglePin = useCallback((slug: string) => {
    setSettings((prev) => {
      const pinned = prev.pinnedCols.includes(slug)
        ? prev.pinnedCols.filter((s) => s !== slug)
        : [...prev.pinnedCols, slug];
      return { ...prev, pinnedCols: pinned };
    });
  }, []);

  const setGrouping = useCallback((grouping: GroupingLevel[]) => {
    setSettings((prev) => ({ ...prev, grouping, groupingTouched: true, collapsedGroups: [] }));
  }, []);

  const addGroupingLevel = useCallback((fieldSlug: string) => {
    setSettings((prev) => {
      if (prev.grouping.some((g) => g.fieldSlug === fieldSlug)) return prev;
      return { ...prev, grouping: [...prev.grouping, { fieldSlug, order: 'name' }], groupingTouched: true, collapsedGroups: [] };
    });
  }, []);

  const removeGroupingLevel = useCallback((fieldSlug: string) => {
    setSettings((prev) => ({
      ...prev,
      grouping: prev.grouping.filter((g) => g.fieldSlug !== fieldSlug),
      groupingTouched: true,
      collapsedGroups: [],
    }));
  }, []);

  const reorderGrouping = useCallback((fromIndex: number, toIndex: number) => {
    setSettings((prev) => {
      const next = [...prev.grouping];
      const [moved] = next.splice(fromIndex, 1);
      if (moved) next.splice(toIndex, 0, moved);
      return { ...prev, grouping: next, groupingTouched: true, collapsedGroups: [] };
    });
  }, []);

  const setGroupingLevelOrder = useCallback((fieldSlug: string, order: 'name' | 'count') => {
    setSettings((prev) => ({
      ...prev,
      grouping: prev.grouping.map((g) => g.fieldSlug === fieldSlug ? { ...g, order } : g),
      groupingTouched: true,
    }));
  }, []);

  const toggleGroupCollapsed = useCallback((compositeKey: string) => {
    setSettings((prev) => {
      const collapsed = prev.collapsedGroups.includes(compositeKey)
        ? prev.collapsedGroups.filter((k) => k !== compositeKey)
        : [...prev.collapsedGroups, compositeKey];
      return { ...prev, collapsedGroups: collapsed };
    });
  }, []);

  // Ordered slugs: pinned first, then custom order (or original), excluding hidden
  const orderedSlugs = (() => {
    const base = settings.colOrder.length > 0
      ? settings.colOrder.filter((s) => fieldSlugs.includes(s))
      : fieldSlugs;
    // Add any new fields not in saved order
    const missing = fieldSlugs.filter((s) => !base.includes(s));
    const all = [...base, ...missing];
    const visible = all.filter((s) => !settings.hiddenCols.includes(s));
    // Pinned first, rest after
    const pinned = visible.filter((s) => settings.pinnedCols.includes(s));
    const unpinned = visible.filter((s) => !settings.pinnedCols.includes(s));
    return [...pinned, ...unpinned];
  })();

  const resetSettings = useCallback(() => {
    // Сброс возвращает группировку к дефолту представления (view.config), а не к пустой.
    setSettings({ colWidths: {}, colOrder: [], hiddenCols: [], pinnedCols: [], grouping: initialGroupingRef.current, groupingTouched: false, collapsedGroups: [], wrapRows: true, density: 'compact', pageSize: 50, showTotals: false, widthMode: 'manual' });
  }, []);

  const toggleWrapRows = useCallback(() => {
    setSettings((prev) => {
      const wrapRows = !prev.wrapRows;
      // Ширины режима 'fit' рассчитаны в расчёте на перенос. Без переноса их
      // пересчитывать нельзя — текст не переносится, а обрезается, — поэтому
      // выключение переноса замораживает текущие ширины.
      const widthMode = !wrapRows && prev.widthMode === 'fit' ? 'manual' : prev.widthMode;
      return { ...prev, wrapRows, widthMode };
    });
  }, []);

  const toggleDensity = useCallback(() => {
    setSettings((prev) => ({ ...prev, density: prev.density === 'compact' ? 'normal' : 'compact' }));
  }, []);

  const toggleShowTotals = useCallback(() => {
    setSettings((prev) => ({ ...prev, showTotals: !prev.showTotals }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setSettings((prev) => ({ ...prev, pageSize }));
  }, []);

  /**
   * Автоподбор ширины колонок.
   *
   * mode='content' — под самый длинный текст в колонке, чтобы он влез в одну
   * строку. mode='fit' — доступная ширина делится между колонками с учётом
   * переноса: см. shared/table/autoWidth.ts.
   *
   * Ищет header-ячейки с data-col-slug, затем по индексу находит body-ячейки.
   */
  const autoSizeColumns = useCallback((options?: AutoSizeOptions) => {
    const mode = options?.mode ?? 'content';
    const types = options?.types ?? {};
    const tableEl = document.querySelector('.lens-items-table');
    if (!tableEl) return;

    // Use header table for slugs, body table for data
    const headerTable = tableEl.querySelector('.ant-table-header table') ?? tableEl.querySelector('table');
    const bodyTable = tableEl.querySelector('.ant-table-body table') ?? headerTable;
    if (!headerTable) return;

    const headerRow = headerTable.querySelector('thead tr');
    if (!headerRow) return;

    const allHeaders = Array.from(headerRow.children) as HTMLElement[];
    const bodyRows = Array.from(bodyTable?.querySelectorAll('tbody tr.ant-table-row') ?? []);
    const { font, fontBold } = cellFonts(bodyRows[0]?.querySelector('td') ?? headerRow);

    const measures: ColumnMeasure[] = [];
    // Колонки без data-col-slug (чекбокс выбора, кнопки) ширину не меняют, но
    // занимают место — вычитаем их из доступной ширины.
    let reserved = 0;

    allHeaders.forEach((th, colIndex) => {
      const slug = th.dataset.colSlug;
      if (!slug) {
        reserved += th.offsetWidth;
        return;
      }
      const headerText = th.textContent ?? '';
      const headerWidth = measureTextWidth(headerText, fontBold) + 40; // padding + grip icon
      const headerWordWidth = measureTextWidth(longestWordOf(headerText), fontBold) + 40;
      const widths: number[] = [];
      let maxContentWidth = 0;
      let longestWord = 0;
      for (const row of bodyRows) {
        const cell = row.children[colIndex] as HTMLElement | undefined;
        if (!cell) continue;
        const text = (cell.textContent ?? '').trim();
        if (!text) continue;
        const cellWidth = measureTextWidth(text, font) + 24;
        widths.push(cellWidth);
        if (cellWidth > maxContentWidth) maxContentWidth = cellWidth;
        const wordWidth = measureTextWidth(longestWordOf(text), font) + 24;
        if (wordWidth > longestWord) longestWord = wordWidth;
      }
      const fieldType = types[slug];
      measures.push({
        slug,
        headerWidth,
        headerWordWidth,
        longestWord: Math.max(longestWord, headerWordWidth),
        contentWidth: Math.max(percentile(widths, 0.9), headerWidth),
        maxContentWidth: Math.max(maxContentWidth, headerWidth),
        wrappable: isWrappable(fieldType),
        cap: capForType(fieldType),
      });
    });

    if (measures.length === 0) return;

    let newWidths: Record<string, number>;
    if (mode === 'fit') {
      // clientWidth уже без вертикального скроллбара; -2 на границы ячеек.
      const available = Math.max(240, tableEl.clientWidth - reserved - 2);
      newWidths = distributeWidths(measures, available);
    } else {
      newWidths = {};
      for (const m of measures) {
        newWidths[m.slug] = Math.max(80, Math.min(m.maxContentWidth, 500));
      }
    }

    setSettings((prev) => ({ ...prev, colWidths: { ...prev.colWidths, ...newWidths }, widthMode: mode }));
  }, []);

  return {
    settings,
    orderedSlugs,
    setColWidth,
    setColOrder,
    toggleColVisibility,
    togglePin,
    setGrouping,
    addGroupingLevel,
    removeGroupingLevel,
    reorderGrouping,
    setGroupingLevelOrder,
    toggleGroupCollapsed,
    resetSettings,
    autoSizeColumns,
    toggleWrapRows,
    toggleDensity,
    toggleShowTotals,
    setPageSize,
  };
}
